import { useNode } from "@craftjs/core";
import { FormControlLabel, FormGroup, Switch, TextField } from "@mui/material";

import { useThemeStore } from "@state/theme";
import { BasicCarouselPreviewComponent } from "../basic/BasicCarouselPreviewComponent";
import { EventFiltersSettings } from "@components/EventFilterSettings";

interface FeaturedCarouselProps {
  title: string;
  description?: string;
  showActionButton?: boolean;
  actionButtonText?: string;
  categoryIDs?: string[];
  eventStatus?: string;
  keywords?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  connect?: any;
  drag?: any;
}

const FeaturedCarouselBuilderComponent = (props: FeaturedCarouselProps) => {
  const {
    connectors: { connect, drag },
  } = useNode();

  return (
    <FeaturedCarouselPreviewComponent
      {...props}
      connect={connect}
      drag={drag}
    />
  );
};

export default FeaturedCarouselBuilderComponent;

export const FeaturedCarouselPreviewComponent = ({
  title,
  description,
  showActionButton,
  actionButtonText,
  connect,
  drag,
  categoryIDs,
  eventStatus,
  keywords,
  startDate,
  endDate,
}: FeaturedCarouselProps) => {
  const { textColor, primaryColor: primaryButtonColor } = useThemeStore();
  // const accentColor = useThemeStore((theme) => theme.accentColor);

  return (
    <div
      ref={(ref: any) => (connect && drag ? connect(drag(ref)) : undefined)}
      className="flex flex-row items-center gap-4 pt-4 pl-4 pb-6"
    >
      <div className="flex flex-col">
        {title && (
          <h2
            className={`text-lg tracking-tight sm:text-xl md:text-2xl mb-3 leading-none font-bold`}
            style={{ color: textColor }}
          >
            {title}
          </h2>
        )}

        <p
          className="line-clamp-3 mb-4 leading-snug"
          style={{ color: textColor }}
        >
          {description}
        </p>

        {showActionButton && (
          <div
            rel="noopener noreferrer"
            className="px-14 w-min mt-2 whitespace-nowrap text-center py-2 block font-bold rounded-md shadow-lg hover:shadow"
            style={{
              color: textColor,
              backgroundColor: primaryButtonColor,
            }}
          >
            {actionButtonText}
          </div>
        )}
      </div>

      <div className="w-3/4">
        <BasicCarouselPreviewComponent
          title=""
          noPadding
          showSeeMore={false}
          categoryIDs={categoryIDs}
          eventStatus={eventStatus}
          keywords={keywords}
          startDate={startDate}
          endDate={endDate}
        />
      </div>
    </div>
  );
};

const FeaturedCarouselSettings = () => {
  const {
    actions: { setProp },
    title,
    description,
    showActionButton,
    actionButtonText,
  } = useNode((node) => ({
    title: node.data.props.title,
    description: node.data.props.description,
    showActionButton: node.data.props.showActionButton,
    actionButtonText: node.data.props.actionButtonText,
  }));

  return (
    <div className="w-full space-y-4">
      <TextField
        fullWidth
        label="Title"
        value={title}
        onChange={(e) => setProp((prop: any) => (prop.title = e.target.value))}
      />

      <TextField
        fullWidth
        label="Description"
        value={description}
        onChange={(e) => setProp((prop: any) => (prop.description = e.target.value))}
      />

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={showActionButton}
              color="primary"
              onChange={(e) => setProp((prop: any) => (prop.showActionButton = e.target.checked))}
            />
          }
          label="Show Action Button"
        />
      </FormGroup>

      {showActionButton && (
        <TextField
          fullWidth
          label="Action Button Text"
          value={actionButtonText}
          onChange={(e) => setProp((prop: any) => (prop.actionButtonText = e.target.value))}
        />
      )}

      <EventFiltersSettings
        includeCategories={true}
        includeEventStatus={true}
        includeKeywords={true}
        includeDateRange={true}
      />
    </div>
  );
};

FeaturedCarouselBuilderComponent.craft = {
  props: {
    title: "Featured Carousel",
    description: "Labore amet tempor nostrud enim sint laborum esse ipsum reprehenderit fugiat sit minim ea. Enim ",
    showActionButton: true,
    actionButtonText: "View Related",
    categoryIDs: [],
    eventStatus: "published",
    keywords: [],
    startDate: null,
    endDate: null,
  },
  displayName: "Featured Carousel",
  related: {
    settings: FeaturedCarouselSettings,
  },
};
